/**
 * Runtime context — manages which lexruntime instance the portal is connected to.
 *
 * In dev mode, defaults to "Local Dev" (127.0.0.1:18789 via Vite proxy).
 * In production, admin selects org → user → connects to that user's runtime.
 *
 * Must be used INSIDE PlatformApiProvider (needs usePlatformApi for instance tokens)
 * and OUTSIDE GatewayProvider (controls the wsUrl prop passed to it).
 */

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { usePlatformApi } from "./usePlatformApi";

export type RuntimeTarget = {
  userId: string;
  orgId: string;
  orgName: string;
  /** Human-readable org slug (kebab-case, e.g. "antoine-abd") shown in
   *  the top-bar alongside the userId so operators see which tenant a
   *  selected runtime belongs to without parsing the ULID. */
  orgSlug: string;
  userName: string;
  /** WebSocket URL for the OpenClaw gateway (ws(s)://host/ws) */
  wsUrl: string;
  /** HTTP base URL for the runtime gateway (http(s)://host) — used by HarnClient for /harn/* */
  httpUrl: string;
  /**
   * The in-band `connect`-frame token the lexruntime gateway expects:
   * the L1-derived per-org `HMAC-SHA256(LEXIUN_SIGNING_KEY,
   * "lexiun-gateway-v1:<orgId>")` (see `gateway-token-convergence-plan`
   * and `lexportal-fleet-runtime-connect-handoff`). Fetched from
   * `GET /api/agent/connect?targetUserId=<userId>` after the
   * instance-token call. Same value in dev and prod (no `"lexdev"`
   * fallback for selected per-user runtimes — they all expect L1).
   */
  token: string;
  /** Lexiun JWT for HTTP plugin routes like /harn/* (always a real JWT, not "lexdev") */
  harnToken: string;
} | null;

type RuntimeContextValue = {
  target: RuntimeTarget;
  selectRuntime: (
    orgId: string,
    userId: string,
    orgName: string,
    userName: string,
    orgSlug: string,
  ) => Promise<void>;
  clearRuntime: () => void;
  isLocalDev: boolean;
  /** WS URL for the GatewayProvider — undefined means use default (local dev proxy) */
  gatewayWsUrl: string | undefined;
  /** Token override for the GatewayProvider — undefined means use default auth */
  gatewayToken: string | undefined;
  loading: boolean;
  error: string | null;
};

const RuntimeContext = createContext<RuntimeContextValue | null>(null);

export function RuntimeContextProvider({ children }: { children: ReactNode }) {
  const api = usePlatformApi();
  const [target, setTarget] = useState<RuntimeTarget>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectRuntime = useCallback(
    async (
      orgId: string,
      userId: string,
      orgName: string,
      userName: string,
      orgSlug: string,
    ) => {
      setLoading(true);
      setError(null);
      try {
        // 1) Instance token → admin URL + the dashboard/harn JWT.
        const res = await api.createInstanceToken({ orgId, targetUserId: userId, adminName: "LexPortal Admin" });
        const data = res.data;
        const urlObj = new URL(data.url);
        const wsProto = urlObj.protocol === "https:" ? "wss:" : "ws:";
        const wsUrl = `${wsProto}//${urlObj.host}/ws`;
        const httpUrl = `${urlObj.protocol}//${urlObj.host}`;

        // 2) Operator-impersonating fetch of the L1-derived per-org
        //    gateway token (caller=PLATFORM_ADMIN, target=`userId`).
        //    Before the convergence fix this used `"lexdev"` (dev) or
        //    the instance-token JWT (prod) as the gateway in-band token
        //    — both rejected by the post-convergence runtime gateway
        //    which expects HMAC(LEXIUN_SIGNING_KEY,
        //    "lexiun-gateway-v1:<orgId>"). See
        //    `ai-docs/lexportal-fleet-runtime-connect-handoff.md`.
        //
        //    No fallback: a missing gateway token = a guaranteed
        //    `INVALID_REQUEST` reconnect loop, so we surface the error
        //    immediately (per `feedback_no_fallbacks`).
        const connectRes = await api.getAgentConnect(userId);
        const gatewayToken = connectRes.data.gatewayToken;
        if (!gatewayToken) {
          throw new Error(
            connectRes.data.gatewayTokenError ??
              "agent/connect did not return a gatewayToken for the target",
          );
        }

        setTarget({
          userId,
          orgId,
          orgName,
          orgSlug,
          userName,
          wsUrl,
          httpUrl,
          token: gatewayToken, // L1-derived; matches what the runtime expects
          harnToken: data.token, // instance-token JWT (accepted by /harn proxy)
        });
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to connect to runtime");
      } finally {
        setLoading(false);
      }
    },
    [api],
  );

  const clearRuntime = useCallback(() => {
    setTarget(null);
    setError(null);
  }, []);

  const isLocalDev = target === null;

  const value = useMemo<RuntimeContextValue>(
    () => ({
      target,
      selectRuntime,
      clearRuntime,
      isLocalDev,
      gatewayWsUrl: target?.wsUrl,
      gatewayToken: target?.token,
      loading,
      error,
    }),
    [target, selectRuntime, clearRuntime, isLocalDev, loading, error],
  );

  return <RuntimeContext value={value}>{children}</RuntimeContext>;
}

export function useRuntimeContext(): RuntimeContextValue {
  const ctx = useContext(RuntimeContext);
  if (!ctx) {
    throw new Error("useRuntimeContext must be used within RuntimeContextProvider");
  }
  return ctx;
}
